import {
  CoachObservation,
  MissingContentVariant,
  WritingAssistContext,
} from './WritingAssistMoments.types';

const PRIORITY: Record<CoachObservation['dimension'], number> = {
  spelling: 0,
  missing_content: 1,
  too_short: 2,
  too_long: 2,
  grammar: 3,
  clarity: 4,
};

const MISSING_BY_CONTEXT: Record<WritingAssistContext, readonly MissingContentVariant[]> = {
  aboutMe: ['looking_for_work', 'personal_note'],
  baseCoverLetter: ['availability', 'experience'],
  applicationCoverLetter: ['availability', 'experience'],
  jobDescription: ['schedule', 'role'],
};

const MISSING_TEMPLATE_BY_CONTEXT: Record<WritingAssistContext, string> = {
  aboutMe: 'WA-K-15',
  baseCoverLetter: 'WA-K-14',
  applicationCoverLetter: 'WA-K-14',
  jobDescription: 'WA-K-16',
};

const CONTEXT_NOUN: Record<WritingAssistContext, string> = {
  aboutMe: 'bios',
  baseCoverLetter: 'cover letters',
  applicationCoverLetter: 'cover letters',
  jobDescription: 'job postings',
};

function isBoundedString(value: unknown, maxLength: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= maxLength;
}

function isIntegerInRange(value: unknown, min: number, max: number): value is number {
  return Number.isSafeInteger(value) && Number(value) >= min && Number(value) <= max;
}

function normalizeOne(
  context: WritingAssistContext,
  raw: Record<string, unknown>,
): CoachObservation | null {
  if (raw.dimension === 'spelling' && raw.templateId === 'WA-K-10'
    && isIntegerInRange(raw.errorCount, 1, 1000)
    && Array.isArray(raw.examples) && raw.examples.length <= 2
    && raw.examples.every(item => isBoundedString(item, 120))) {
    return {
      dimension: 'spelling',
      templateId: 'WA-K-10',
      errorCount: raw.errorCount,
      examples: raw.examples as string[],
    };
  }
  if (raw.dimension === 'grammar' && raw.templateId === 'WA-K-11'
    && isIntegerInRange(raw.issueCount, 1, 1000)
    && isBoundedString(raw.example, 120)) {
    return {
      dimension: 'grammar',
      templateId: 'WA-K-11',
      issueCount: raw.issueCount,
      example: raw.example,
    };
  }
  if ((raw.dimension === 'too_short' || raw.dimension === 'too_long')
    && raw.templateId === (raw.dimension === 'too_short' ? 'WA-K-12' : 'WA-K-13')
    && isIntegerInRange(raw.wordCount, 0, 10000)
    && isIntegerInRange(raw.minWords, 1, 5000)
    && isIntegerInRange(raw.maxWords, 1, 5000)
    && raw.minWords < raw.maxWords
    && ((raw.dimension === 'too_short' && raw.wordCount < raw.minWords)
      || (raw.dimension === 'too_long' && raw.wordCount > raw.maxWords))) {
    return raw.dimension === 'too_short'
      ? {
        dimension: 'too_short', templateId: 'WA-K-12', wordCount: raw.wordCount,
        minWords: raw.minWords, maxWords: raw.maxWords,
      }
      : {
        dimension: 'too_long', templateId: 'WA-K-13', wordCount: raw.wordCount,
        minWords: raw.minWords, maxWords: raw.maxWords,
      };
  }
  if (raw.dimension === 'missing_content'
    && typeof raw.variant === 'string'
    && MISSING_BY_CONTEXT[context].indexOf(raw.variant as MissingContentVariant) !== -1
    && raw.templateId === MISSING_TEMPLATE_BY_CONTEXT[context]) {
    return {
      dimension: 'missing_content',
      templateId: raw.templateId as 'WA-K-14' | 'WA-K-15' | 'WA-K-16',
      variant: raw.variant as MissingContentVariant,
    };
  }
  if (raw.dimension === 'clarity' && raw.templateId === 'WA-K-17'
    && isBoundedString(raw.prefix, 80)) {
    return { dimension: 'clarity', templateId: 'WA-K-17', prefix: raw.prefix };
  }
  return null;
}

export function normalizeCoachObservations(
  context: WritingAssistContext,
  observations: unknown,
): CoachObservation[] {
  if (!Array.isArray(observations)) return [];
  const byDimension = new Map<CoachObservation['dimension'], CoachObservation>();
  observations.forEach((raw) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;
    const observation = normalizeOne(context, raw as Record<string, unknown>);
    if (observation && !byDimension.has(observation.dimension)) {
      byDimension.set(observation.dimension, observation);
    }
  });
  return Array.from(byDimension.values())
    .sort((left, right) => PRIORITY[left.dimension] - PRIORITY[right.dimension])
    .slice(0, 3);
}

const MISSING_COPY: Record<MissingContentVariant, string> = {
  availability: "it doesn't mention your availability",
  experience: "it doesn't mention your experience",
  looking_for_work: "it doesn't say what kind of work you're looking for",
  personal_note: "it's all credentials — offices also warm to a personal note",
  schedule: "it doesn't mention the schedule or days",
  role: "it doesn't say much about the role itself",
};

export function renderCoachObservation(
  context: WritingAssistContext,
  observation: CoachObservation,
): string {
  switch (observation.dimension) {
    case 'spelling':
      return observation.examples.length
        ? `a few spelling slips (${observation.examples.join(', ')})`
        : 'a few spelling slips';
    case 'grammar':
      return `some phrasing that may read as rushed (${observation.example})`;
    case 'too_short':
      return `it's brief at ${observation.wordCount} words — strong ${CONTEXT_NOUN[context]} usually run ${observation.minWords}–${observation.maxWords} words`;
    case 'too_long':
      return `it's long at ${observation.wordCount} words — the strongest stay under ${observation.maxWords}`;
    case 'missing_content':
      return MISSING_COPY[observation.variant];
    case 'clarity':
      return `one sentence is hard to follow (${observation.prefix}…)`;
    default:
      return '';
  }
}
